import * as i2 from '@angular/cdk/dialog';
import { CdkDialogContainer, Dialog, DialogModule } from '@angular/cdk/dialog';
import { CdkPortalOutlet, PortalModule } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, ViewEncapsulation, Optional, Inject, InjectionToken, Injectable, SkipSelf, NgModule } from '@angular/core';
import { AnimationDurations, AnimationCurves, MatCommonModule } from '@angular/material/core';
import * as i1 from '@angular/cdk/a11y';
import * as i4 from '@angular/cdk/layout';
import { Breakpoints } from '@angular/cdk/layout';
import * as i3 from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { trigger, state, style, transition, group, animate, query, animateChild } from '@angular/animations';
import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { Subject, merge } from 'rxjs';
import { filter, take } from 'rxjs/operators';

/** Animations used by the Material bottom sheet. */
function MatBottomSheetContainer_ng_template_0_Template(rf, ctx) {}
const matBottomSheetAnimations = {
  /** Animation that shows and hides a bottom sheet. */
  bottomSheetState: /*#__PURE__*/trigger('state', [/*#__PURE__*/state('void, hidden', /*#__PURE__*/style({
    transform: 'translateY(100%)'
  })), /*#__PURE__*/state('visible', /*#__PURE__*/style({
    transform: 'translateY(0%)'
  })), /*#__PURE__*/transition('visible => void, visible => hidden', /*#__PURE__*/group([/*#__PURE__*/animate(`${AnimationDurations.COMPLEX} ${AnimationCurves.ACCELERATION_CURVE}`), /*#__PURE__*/query('@*', /*#__PURE__*/animateChild(), {
    optional: true
  })])), /*#__PURE__*/transition('void => visible', /*#__PURE__*/group([/*#__PURE__*/animate(`${AnimationDurations.EXITING} ${AnimationCurves.DECELERATION_CURVE}`), /*#__PURE__*/query('@*', /*#__PURE__*/animateChild(), {
    optional: true
  })]))])
};

/**
 * Internal component that wraps user-provided bottom sheet content.
 * @docs-private
 */
let MatBottomSheetContainer = /*#__PURE__*/(() => {
  class MatBottomSheetContainer extends CdkDialogContainer {
    constructor(elementRef, focusTrapFactory, document, config, checker, ngZone, overlayRef, breakpointObserver, focusMonitor) {
      super(elementRef, focusTrapFactory, document, config, checker, ngZone, overlayRef, focusMonitor);
      /** The state of the bottom sheet animations. */
      this._animationState = 'void';
      /** Emits whenever the state of the animation changes. */
      this._animationStateChanged = new EventEmitter();
      this._breakpointSubscription = breakpointObserver.observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge]).subscribe(() => {
        this._toggleClass('mat-bottom-sheet-container-medium', breakpointObserver.isMatched(Breakpoints.Medium));
        this._toggleClass('mat-bottom-sheet-container-large', breakpointObserver.isMatched(Breakpoints.Large));
        this._toggleClass('mat-bottom-sheet-container-xlarge', breakpointObserver.isMatched(Breakpoints.XLarge));
      });
    }
    /** Begin animation of bottom sheet entrance into view. */
    enter() {
      if (!this._destroyed) {
        this._animationState = 'visible';
        this._changeDetectorRef.detectChanges();
      }
    }
    /** Begin animation of the bottom sheet exiting from view. */
    exit() {
      if (!this._destroyed) {
        this._animationState = 'hidden';
        this._changeDetectorRef.markForCheck();
      }
    }
    ngOnDestroy() {
      super.ngOnDestroy();
      this._breakpointSubscription.unsubscribe();
      this._destroyed = true;
    }
    _onAnimationDone(event) {
      if (event.toState === 'visible') {
        this._trapFocus();
      }
      this._animationStateChanged.emit(event);
    }
    _onAnimationStart(event) {
      this._animationStateChanged.emit(event);
    }
    _captureInitialFocus() {}
    _toggleClass(cssClass, add) {
      this._elementRef.nativeElement.classList.toggle(cssClass, add);
    }
    static {
      this.ɵfac = function MatBottomSheetContainer_Factory(t) {
        return new (t || MatBottomSheetContainer)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.FocusTrapFactory), i0.ɵɵdirectiveInject(DOCUMENT, 8), i0.ɵɵdirectiveInject(i2.DialogConfig), i0.ɵɵdirectiveInject(i1.InteractivityChecker), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i3.OverlayRef), i0.ɵɵdirectiveInject(i4.BreakpointObserver), i0.ɵɵdirectiveInject(i1.FocusMonitor));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: MatBottomSheetContainer,
        selectors: [["mat-bottom-sheet-container"]],
        hostAttrs: ["tabindex", "-1", 1, "mat-bottom-sheet-container"],
        hostVars: 4,
        hostBindings: function MatBottomSheetContainer_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵsyntheticHostListener("@state.start", function MatBottomSheetContainer_animation_state_start_HostBindingHandler($event) {
              return ctx._onAnimationStart($event);
            })("@state.done", function MatBottomSheetContainer_animation_state_done_HostBindingHandler($event) {
              return ctx._onAnimationDone($event);
            });
          }
          if (rf & 2) {
            i0.ɵɵsyntheticHostProperty("@state", ctx._animationState);
            i0.ɵɵattribute("role", ctx._config.role)("aria-modal", ctx._config.ariaModal)("aria-label", ctx._config.ariaLabel);
          }
        },
        standalone: true,
        features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵStandaloneFeature],
        decls: 1,
        vars: 0,
        consts: [["cdkPortalOutlet", ""]],
        template: function MatBottomSheetContainer_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, MatBottomSheetContainer_ng_template_0_Template, 0, 0, "ng-template", 0);
          }
        },
        dependencies: [CdkPortalOutlet],
        styles: [".mat-bottom-sheet-container{box-shadow:0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12);padding:8px 16px;min-width:100vw;box-sizing:border-box;display:block;outline:0;max-height:80vh;overflow:auto;background:var(--mat-bottom-sheet-container-background-color);color:var(--mat-bottom-sheet-container-text-color);font-family:var(--mat-bottom-sheet-container-text-font);font-size:var(--mat-bottom-sheet-container-text-size);line-height:var(--mat-bottom-sheet-container-text-line-height);font-weight:var(--mat-bottom-sheet-container-text-weight);letter-spacing:var(--mat-bottom-sheet-container-text-tracking)}.cdk-high-contrast-active .mat-bottom-sheet-container{outline:1px solid}.mat-bottom-sheet-container-xlarge,.mat-bottom-sheet-container-large,.mat-bottom-sheet-container-medium{border-top-left-radius:var(--mat-bottom-sheet-container-shape);border-top-right-radius:var(--mat-bottom-sheet-container-shape)}.mat-bottom-sheet-container-medium{min-width:384px;max-width:calc(100vw - 128px)}.mat-bottom-sheet-container-large{min-width:512px;max-width:calc(100vw - 256px)}.mat-bottom-sheet-container-xlarge{min-width:576px;max-width:calc(100vw - 384px)}"],
        encapsulation: 2,
        data: {
          animation: [matBottomSheetAnimations.bottomSheetState]
        }
      });
    }
  }
  return MatBottomSheetContainer;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/** Injection token that can be used to access the data that was passed in to a bottom sheet. */
const MAT_BOTTOM_SHEET_DATA = /*#__PURE__*/new InjectionToken('MatBottomSheetData');
/**
 * Configuration used when opening a bottom sheet.
 */
class MatBottomSheetConfig {
  constructor() {
    /** Data being injected into the child component. */
    this.data = null;
    /** Whether the bottom sheet has a backdrop. */
    this.hasBackdrop = true;
    /** Whether the user can use escape or clicking outside to close the bottom sheet. */
    this.disableClose = false;
    /** Aria label to assign to the bottom sheet element. */
    this.ariaLabel = null;
    /** Whether this is a modal bottom sheet. Used to set the `aria-modal` attribute. */
    this.ariaModal = true;
    /**
     * Whether the bottom sheet should close when the user goes backwards/forwards in history.
     * Note that this usually doesn't include clicking on links (unless the user is using
     * the `HashLocationStrategy`).
     */
    this.closeOnNavigation = true;
    // Note that this is set to 'dialog' by default, because while the a11y recommendations
    // are to focus the first focusable element, doing so prevents screen readers from reading out the
    // rest of the bottom sheet content.
    /**
     * Where the bottom sheet should focus on open.
     * @breaking-change 14.0.0 Remove boolean option from autoFocus. Use string or
     * AutoFocusTarget instead.
     */
    this.autoFocus = 'dialog';
    /**
     * Whether the bottom sheet should restore focus to the
     * previously-focused element, after it's closed.
     */
    this.restoreFocus = true;
  }
}

/**
 * Reference to a bottom sheet dispatched from the bottom sheet service.
 */
class MatBottomSheetRef {
  /** Instance of the component making up the content of the bottom sheet. */
  get instance() {
    return this._ref.componentInstance;
  }
  /**
   * `ComponentRef` of the component opened into the bottom sheet. Will be
   * null when the bottom sheet is opened using a `TemplateRef`.
   */
  get componentRef() {
    return this._ref.componentRef;
  }
  constructor(_ref, config, containerInstance) {
    this._ref = _ref;
    /** Subject for notifying the user that the bottom sheet has opened and appeared. */
    this._afterOpened = new Subject();
    this.containerInstance = containerInstance;
    this.disableClose = config.disableClose;
    // Emit when opening animation completes
    containerInstance._animationStateChanged.pipe(filter(event => event.phaseName === 'done' && event.toState === 'visible'), take(1)).subscribe(() => {
      this._afterOpened.next();
      this._afterOpened.complete();
    });
    // Dispose overlay when closing animation is complete
    containerInstance._animationStateChanged.pipe(filter(event => event.phaseName === 'done' && event.toState === 'hidden'), take(1)).subscribe(() => {
      clearTimeout(this._closeFallbackTimeout);
      this._ref.close(this._result);
    });
    _ref.overlayRef.detachments().subscribe(() => {
      this._ref.close(this._result);
    });
    merge(this.backdropClick(), this.keydownEvents().pipe(filter(event => event.keyCode === ESCAPE))).subscribe(event => {
      if (!this.disableClose && (event.type !== 'keydown' || !hasModifierKey(event))) {
        event.preventDefault();
        this.dismiss();
      }
    });
  }
  /**
   * Dismisses the bottom sheet.
   * @param result Data to be passed back to the bottom sheet opener.
   */
  dismiss(result) {
    if (!this.containerInstance) {
      return;
    }
    // Transition the backdrop in parallel to the bottom sheet.
    this.containerInstance._animationStateChanged.pipe(filter(event => event.phaseName === 'start'), take(1)).subscribe(event => {
      // The logic that disposes of the overlay depends on the exit animation completing, however
      // it isn't guaranteed if the parent view is destroyed while it's running. Add a fallback
      // timeout which will clean everything up if the animation hasn't fired within the specified
      // amount of time plus 100ms. We don't need to run this outside the NgZone, because for the
      // vast majority of cases the timeout will have been cleared before it has fired.
      this._closeFallbackTimeout = setTimeout(() => {
        this._ref.close(this._result);
      }, event.totalTime + 100);
      this._ref.overlayRef.detachBackdrop();
    });
    this._result = result;
    this.containerInstance.exit();
    this.containerInstance = null;
  }
  /** Gets an observable that is notified when the bottom sheet is finished closing. */
  afterDismissed() {
    return this._ref.closed;
  }
  /** Gets an observable that is notified when the bottom sheet has opened and appeared. */
  afterOpened() {
    return this._afterOpened;
  }
  /**
   * Gets an observable that emits when the overlay's backdrop has been clicked.
   */
  backdropClick() {
    return this._ref.backdropClick;
  }
  /**
   * Gets an observable that emits when keydown events are targeted on the overlay.
   */
  keydownEvents() {
    return this._ref.keydownEvents;
  }
}

/** Injection token that can be used to specify default bottom sheet options. */
const MAT_BOTTOM_SHEET_DEFAULT_OPTIONS = /*#__PURE__*/new InjectionToken('mat-bottom-sheet-default-options');
/**
 * Service to trigger Material Design bottom sheets.
 */
let MatBottomSheet = /*#__PURE__*/(() => {
  class MatBottomSheet {
    /** Reference to the currently opened bottom sheet. */
    get _openedBottomSheetRef() {
      const parent = this._parentBottomSheet;
      return parent ? parent._openedBottomSheetRef : this._bottomSheetRefAtThisLevel;
    }
    set _openedBottomSheetRef(value) {
      if (this._parentBottomSheet) {
        this._parentBottomSheet._openedBottomSheetRef = value;
      } else {
        this._bottomSheetRefAtThisLevel = value;
      }
    }
    constructor(_overlay, injector, _parentBottomSheet, _defaultOptions) {
      this._overlay = _overlay;
      this._parentBottomSheet = _parentBottomSheet;
      this._defaultOptions = _defaultOptions;
      this._bottomSheetRefAtThisLevel = null;
      this._dialog = injector.get(Dialog);
    }
    open(componentOrTemplateRef, config) {
      const _config = {
        ...(this._defaultOptions || new MatBottomSheetConfig()),
        ...config
      };
      let ref;
      this._dialog.open(componentOrTemplateRef, {
        ..._config,
        // Disable closing since we need to sync it up to the animation ourselves.
        disableClose: true,
        // Disable closing on detachments so that we can sync up the animation.
        closeOnOverlayDetachments: false,
        maxWidth: '100%',
        container: MatBottomSheetContainer,
        scrollStrategy: _config.scrollStrategy || this._overlay.scrollStrategies.block(),
        positionStrategy: this._overlay.position().global().centerHorizontally().bottom('0'),
        templateContext: () => ({
          bottomSheetRef: ref
        }),
        providers: (cdkRef, _cdkConfig, container) => {
          ref = new MatBottomSheetRef(cdkRef, _config, container);
          return [{
            provide: MatBottomSheetRef,
            useValue: ref
          }, {
            provide: MAT_BOTTOM_SHEET_DATA,
            useValue: _config.data
          }];
        }
      });
      // When the bottom sheet is dismissed, clear the reference to it.
      ref.afterDismissed().subscribe(() => {
        // Clear the bottom sheet ref if it hasn't already been replaced by a newer one.
        if (this._openedBottomSheetRef === ref) {
          this._openedBottomSheetRef = null;
        }
      });
      if (this._openedBottomSheetRef) {
        // If a bottom sheet is already in view, dismiss it and enter the
        // new bottom sheet after exit animation is complete.
        this._openedBottomSheetRef.afterDismissed().subscribe(() => ref.containerInstance?.enter());
        this._openedBottomSheetRef.dismiss();
      } else {
        // If no bottom sheet is in view, enter the new bottom sheet.
        ref.containerInstance.enter();
      }
      this._openedBottomSheetRef = ref;
      return ref;
    }
    /**
     * Dismisses the currently-visible bottom sheet.
     * @param result Data to pass to the bottom sheet instance.
     */
    dismiss(result) {
      if (this._openedBottomSheetRef) {
        this._openedBottomSheetRef.dismiss(result);
      }
    }
    ngOnDestroy() {
      if (this._bottomSheetRefAtThisLevel) {
        this._bottomSheetRefAtThisLevel.dismiss();
      }
    }
    static {
      this.ɵfac = function MatBottomSheet_Factory(t) {
        return new (t || MatBottomSheet)(i0.ɵɵinject(i3.Overlay), i0.ɵɵinject(i0.Injector), i0.ɵɵinject(MatBottomSheet, 12), i0.ɵɵinject(MAT_BOTTOM_SHEET_DEFAULT_OPTIONS, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MatBottomSheet,
        factory: MatBottomSheet.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MatBottomSheet;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let MatBottomSheetModule = /*#__PURE__*/(() => {
  class MatBottomSheetModule {
    static {
      this.ɵfac = function MatBottomSheetModule_Factory(t) {
        return new (t || MatBottomSheetModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: MatBottomSheetModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [MatBottomSheet],
        imports: [DialogModule, MatCommonModule, PortalModule, MatCommonModule]
      });
    }
  }
  return MatBottomSheetModule;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { MAT_BOTTOM_SHEET_DATA, MAT_BOTTOM_SHEET_DEFAULT_OPTIONS, MatBottomSheet, MatBottomSheetConfig, MatBottomSheetContainer, MatBottomSheetModule, MatBottomSheetRef, matBottomSheetAnimations };
